import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReAuthModal } from '../../components/ReAuthModal';
import { useUserContext } from '../../hooks/useUserContext';
import { COUNTRIES, type CountryOption } from '../../data/countries';
import { INDUSTRIES } from '../../data/industries';
import type { Profile } from '../../types/profile';
import { getComplianceFramework } from '../../lib/countryCompliance';
import { supabase } from '../../lib/supabase';
import {
  cardClass,
  inputClass,
  labelClass,
  lockedFieldShellStyle,
  settingsHelperMutedStyle,
} from './styles';
import { IconLock14, IconPencil14 } from './SecurityFieldIcons';

const CURRENCIES: { value: string; label: string }[] = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'NGN', label: 'NGN — Nigerian Naira' },
  { value: 'GHS', label: 'GHS — Ghanaian Cedi' },
  { value: 'KES', label: 'KES — Kenyan Shilling' },
  { value: 'ZAR', label: 'ZAR — South African Rand' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'BRL', label: 'BRL — Brazilian Real' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'OTHER', label: 'Other' },
];

function countryFromProfile(countryLabel: string): CountryOption | null {
  const t = countryLabel.trim();
  if (!t) return null;
  return COUNTRIES.find((c) => c.name === t) ?? null;
}

type Props = {
  userProfile: Profile | null;
  businessEmail: string;
  userId: string;
  onToast: (message: string, variant: 'success' | 'error') => void;
  onProfileSaved: () => Promise<void>;
};

export function BusinessProfileTab({
  userProfile,
  businessEmail,
  userId,
  onToast,
  onProfileSaved,
}: Props) {
  const { isPersonal } = useUserContext();

  const [businessName, setBusinessName] = useState('');
  const [businessNameUnlocked, setBusinessNameUnlocked] = useState(false);
  const [reAuthBusinessOpen, setReAuthBusinessOpen] = useState(false);
  const [savingBusinessName, setSavingBusinessName] = useState(false);

  const [ownerName, setOwnerName] = useState('');
  const [region, setRegion] = useState('');
  const [industry, setIndustry] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [saving, setSaving] = useState(false);

  const currencyOptions = useMemo(() => {
    const cur = userProfile?.currency?.trim();
    if (cur && !CURRENCIES.some((c) => c.value === cur)) {
      return [...CURRENCIES, { value: cur, label: `${cur} (current)` }];
    }
    return CURRENCIES;
  }, [userProfile?.currency]);

  const [errors, setErrors] = useState<{
    businessName?: string;
    ownerName?: string;
    industry?: string;
    currency?: string;
  }>({});

  const countryDisplay = userProfile?.country?.trim() || '—';

  useEffect(() => {
    if (!userProfile) return;
    setBusinessName(userProfile.business_name ?? '');
    setOwnerName(userProfile.owner_name ?? '');
    setRegion(userProfile.region ?? '');
    setIndustry(userProfile.industry ?? '');
    const raw = userProfile.currency?.trim();
    setCurrency(raw || 'USD');
    setBusinessNameUnlocked(false);
  }, [userProfile]);

  function validate(): boolean {
    const next: typeof errors = {};
    const on = ownerName.trim();
    if (!on) {
      next.ownerName = isPersonal ? 'Enter your name.' : "Enter the owner's full name.";
    }
    if (!isPersonal && !industry) next.industry = 'Select your industry.';
    if (!currency) next.currency = 'Select a currency.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const co = countryFromProfile(userProfile?.country ?? '');
    const framework = co
      ? getComplianceFramework(co.code)
      : (userProfile?.compliance_framework?.trim() ?? '');

    setSaving(true);
    const payload = {
      owner_name: ownerName.trim(),
      region: region.trim() || null,
      industry: isPersonal ? 'Individual' : industry,
      currency: currency === 'OTHER' ? 'OTHER' : currency,
      compliance_framework: framework,
    };

    const { error } = await supabase.from('profiles').update(payload).eq('id', userId);

    setSaving(false);
    if (error) {
      onToast(error.message || 'Could not save profile.', 'error');
      return;
    }

    onToast('Changes saved', 'success');
    await onProfileSaved();
  }

  async function handleSaveBusinessName() {
    const bn = businessName.trim();
    if (!bn) {
      const msg = isPersonal ? 'Enter your full name.' : 'Enter your business name.';
      setErrors((prev) => ({ ...prev, businessName: msg }));
      onToast(msg, 'error');
      return;
    }
    setErrors((prev) => ({ ...prev, businessName: undefined }));
    setSavingBusinessName(true);
    const { error } = await supabase.from('profiles').update({ business_name: bn }).eq('id', userId);
    setSavingBusinessName(false);
    if (error) {
      onToast(error.message || (isPersonal ? 'Could not update full name.' : 'Could not update business name.'), 'error');
      return;
    }
    setBusinessNameUnlocked(false);
    onToast(isPersonal ? 'Full name updated' : 'Business name updated', 'success');
    await onProfileSaved();
  }

  function cancelBusinessNameEdit() {
    setBusinessName(userProfile?.business_name ?? '');
    setBusinessNameUnlocked(false);
    setErrors((prev) => ({ ...prev, businessName: undefined }));
  }

  return (
    <div className={cardClass}>
      <h2 className="text-lg font-semibold text-foreground sm:text-xl">
        {isPersonal ? 'Personal Profile' : 'Business Profile'}
      </h2>
      <p className="mt-1 text-sm text-muted">
        {isPersonal ? 'Keep your personal details up to date.' : 'Keep your company details up to date.'}
      </p>
      <p className="mt-3 text-sm">
        <Link to="/assets" className="font-semibold text-accent hover:underline">
          Manage your monitored assets →
        </Link>
      </p>

      <ReAuthModal
        open={reAuthBusinessOpen}
        onClose={() => setReAuthBusinessOpen(false)}
        onSuccess={() => setBusinessNameUnlocked(true)}
        fieldName={isPersonal ? 'full name' : 'business name'}
        userEmail={businessEmail}
      />

      <form onSubmit={(e) => void handleSave(e)} className="mt-6 space-y-4" noValidate>
        <div>
          <label htmlFor="settings-business-name" className={labelClass}>
            {isPersonal ? 'Full Name' : 'Business Name'}
          </label>
          {!businessNameUnlocked ? (
            <>
              <div style={lockedFieldShellStyle} className="w-full">
                <span className="min-w-0 truncate text-sm">{businessName || '—'}</span>
                <button
                  type="button"
                  onClick={() => setReAuthBusinessOpen(true)}
                  className="shrink-0 rounded p-1 transition hover:bg-foreground/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  title="Click to edit — requires password confirmation"
                  aria-label={isPersonal ? 'Edit full name' : 'Edit business name'}
                >
                  <IconPencil14 style={{ color: 'var(--accent-green)' }} />
                </button>
              </div>
              {errors.businessName ? (
                <p className="mt-1.5 text-sm text-danger">{errors.businessName}</p>
              ) : null}
            </>
          ) : (
            <div className="space-y-2">
              <input
                id="settings-business-name"
                value={businessName}
                onChange={(e) => {
                  setBusinessName(e.target.value);
                  setErrors((prev) => ({ ...prev, businessName: undefined }));
                }}
                className={`${inputClass} border-2 border-accent ring-1 ring-accent/30`}
                autoComplete="organization"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={savingBusinessName}
                  onClick={() => void handleSaveBusinessName()}
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:opacity-60"
                >
                  {savingBusinessName ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={savingBusinessName}
                  onClick={cancelBusinessNameEdit}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="settings-owner-name" className={labelClass}>
            {isPersonal ? 'Your Name' : 'Owner Full Name'}
          </label>
          <input
            id="settings-owner-name"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            className={`${inputClass} ${errors.ownerName ? 'border-danger' : ''}`}
            autoComplete="name"
          />
          {errors.ownerName ? <p className="mt-1.5 text-sm text-danger">{errors.ownerName}</p> : null}
        </div>

        <div>
          <span className={labelClass}>{isPersonal ? 'Email' : 'Business Email'}</span>
          <div style={lockedFieldShellStyle} className="w-full" title="This field cannot be changed">
            <span className="min-w-0 truncate text-sm">{businessEmail || '—'}</span>
            <span className="shrink-0" style={{ color: 'var(--text-tertiary)' }}>
              <IconLock14 />
            </span>
          </div>
          <p className="mt-1.5" style={settingsHelperMutedStyle}>
            To change your email address contact hello@lognsec.com. We will verify ownership of both
            addresses before making any changes.
          </p>
        </div>

        <div>
          <span className={labelClass}>Country</span>
          <div style={lockedFieldShellStyle} className="w-full" title="This field cannot be changed">
            <span className="min-w-0 truncate text-sm">{countryDisplay}</span>
            <span className="shrink-0" style={{ color: 'var(--text-tertiary)' }}>
              <IconLock14 />
            </span>
          </div>
          <p className="mt-1.5" style={settingsHelperMutedStyle}>
            Country cannot be changed after account setup. Contact hello@lognsec.com if you need to
            update this.
          </p>
        </div>

        <div>
          <label htmlFor="settings-region" className={labelClass}>
            Region / State <span className="font-normal text-muted">(optional)</span>
          </label>
          <input
            id="settings-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className={inputClass}
            autoComplete="address-level1"
          />
        </div>

        {!isPersonal ? (
          <div>
            <label htmlFor="settings-industry" className={labelClass}>
              Industry
            </label>
            <select
              id="settings-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className={`${inputClass} appearance-none bg-navy ${errors.industry ? 'border-danger' : ''}`}
            >
              <option value="">Select industry</option>
              {INDUSTRIES.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.industry ? <p className="mt-1.5 text-sm text-danger">{errors.industry}</p> : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="settings-currency" className={labelClass}>
            Primary Currency
          </label>
          <select
            id="settings-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={`${inputClass} appearance-none bg-navy ${errors.currency ? 'border-danger' : ''}`}
          >
            {currencyOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {errors.currency ? <p className="mt-1.5 text-sm text-danger">{errors.currency}</p> : null}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="mt-2 flex w-full min-h-[48px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}
